import type { CongaTestBatchStreamEvent, CongaTestRow } from '@/services/congaTestApi';
import { useEffect, useState } from 'react';

interface UseCongaBatchStreamResult {
  rows: CongaTestRow[];
  isConnected: boolean;
  isFinished: boolean;
  error?: string;
}

const upsertRow = (rows: CongaTestRow[], next: CongaTestRow): CongaTestRow[] => {
  const key = String(next.id);
  const exists = rows.some((row) => String(row.id) === key);
  const updated = exists ? rows.map((row) => (String(row.id) === key ? next : row)) : [...rows, next];
  return updated.sort((a, b) => a.rowNumber - b.rowNumber);
};

/**
 * Subscribes to a batch's SSE stream. A snapshot replaces all rows; `row.updated` upserts one.
 * On `batch.finished` the source is closed explicitly, otherwise EventSource would auto-reconnect.
 */
export const useCongaBatchStream = (batchId: string | undefined): UseCongaBatchStreamResult => {
  const [rows, setRows] = useState<CongaTestRow[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!batchId) return;

    setRows([]);
    setIsFinished(false);
    setError(undefined);

    const source = new EventSource(`/api/internal/conga-test-envelopes/batches/${batchId}/events`);

    const handleOpen = (): void => {
      setIsConnected(true);
    };

    const handleMessage = (evt: MessageEvent<string>): void => {
      try {
        const payload = JSON.parse(evt.data) as CongaTestBatchStreamEvent;
        if (payload.type === 'batch.snapshot') {
          setRows([...payload.rows].sort((a, b) => a.rowNumber - b.rowNumber));
        } else if (payload.type === 'row.updated') {
          setRows((prev) => upsertRow(prev, payload.row));
        } else if (payload.type === 'batch.finished') {
          setIsFinished(true);
          setIsConnected(false);
          source.close();
        } else if (payload.type === 'batch.error') {
          setError(payload.message);
          setIsFinished(true);
          setIsConnected(false);
          source.close();
        }
      } catch {
        // A malformed frame must not poison the subscription.
      }
    };

    const handleError = (): void => {
      setIsConnected(false);
      // A non-200 response (unknown batch, expired session) closes the source for good; EventSource won't retry.
      if (source.readyState === EventSource.CLOSED) {
        setError('Lost connection to the batch stream. Refresh to retry.');
        setIsFinished(true);
      }
    };

    source.addEventListener('open', handleOpen);
    source.addEventListener('message', handleMessage);
    source.addEventListener('error', handleError);

    return (): void => {
      source.removeEventListener('open', handleOpen);
      source.removeEventListener('message', handleMessage);
      source.removeEventListener('error', handleError);
      source.close();
      setIsConnected(false);
    };
  }, [batchId]);

  return { rows, isConnected, isFinished, error };
};
